/**
 * @fileoverview Stale time configuration for React Query
 *
 * Centralizes cache timing configuration to ensure consistency
 * across the application and make tuning easier.
 */

/** Time constants in milliseconds */
const SECOND = 1000;
const MINUTE = 60 * SECOND;

/**
 * Stale time configurations for different data types
 *
 * - IMMEDIATE: Data that should always be fresh (0ms)
 * - SHORT: Frequently changing data (1 minute)
 * - MEDIUM: Standard data (5 minutes) - default
 * - LONG: Rarely changing data (30 minutes)
 * - STATIC: Almost never changes (1 hour)
 */
export const STALE_TIMES = {
  /** Always refetch - real-time data */
  IMMEDIATE: 0,

  /** 1 minute - notifications, unread counts */
  SHORT: 1 * MINUTE,

  /** 5 minutes - user data, forms, meetings (default) */
  MEDIUM: 5 * MINUTE,

  /** 30 minutes - configuration, static lists */
  LONG: 30 * MINUTE,

  /** 1 hour - CMS content, rarely changing data */
  STATIC: 60 * MINUTE,
} as const;

/**
 * Garbage collection time configurations
 * GC time should typically be longer than stale time
 */
export const GC_TIMES = {
  /** 5 minutes */
  SHORT: 5 * MINUTE,

  /** 10 minutes (default) */
  MEDIUM: 10 * MINUTE,

  /** 1 hour */
  LONG: 60 * MINUTE,
} as const;

/**
 * Per-domain stale time recommendations
 * Use these when configuring queries for specific domains
 */
export const DOMAIN_STALE_TIMES = {
  notifications: STALE_TIMES.SHORT,
  users: STALE_TIMES.MEDIUM,
  forms: STALE_TIMES.MEDIUM,
  meetings: STALE_TIMES.MEDIUM,
  cms: STALE_TIMES.STATIC,
  config: STALE_TIMES.LONG,
  dashboard: STALE_TIMES.MEDIUM,
  images: STALE_TIMES.LONG,
} as const;
